import { ApplicationData, EvaluateOptions, Issue, IssuePath, Nullable, Page, RawIssue, Rule, RuleContext, At, owned, present } from './types'
import { message } from './messages'
import { DateProblem, DateResolution, dateProblem, findGapsInMonthRanges, isOver16, isUnder110, yearsCoveredSince } from './dates'
import { isValidPostcode } from './postcode'
import { isValidNationalInsuranceNumber } from './nationalInsurance'
import { isValidDriverLicenceNumber } from './driverLicence'
import { NAME_PLACEHOLDER_TOKENS, PLACEHOLDER_TOKENS, placeholderIn } from './placeholders'
import { countyCountryProblemKind } from './countyNotCountry'
import { findNameWarnings } from './nameWarnings'
import { fieldConstraintIssues, requiredFieldIssues, upperCaseIssues } from './fields'

/**
 * The consolidated DBS validation rule set (DBS-344): one list of rules, each tagged with the DBS error codes and
 * specification rows it answers, run identically by every gate (applicant pages and confirm screen, staff
 * correction, countersigning, resend and the batch build) through `evaluateRules`.
 *
 * Three layers, in the order they run:
 * 1. the helpful SmartDBS checks that should win over a bare character-set message (a country typed as a county);
 * 2. the XSD layer, generated from the DBS's schema (fieldSpec.ts): lengths, patterns, permitted values;
 * 3. the business rules: the DBS's section-4 list, its documented formats (NI number, postcode, driver licence number)
 *    and our own extras (placeholders, title/gender, English only, no fixed abode outside the UK).
 * Required-element, upper-case, identity-verified and Registered Body checks run only on the server (`at: 'server'`).
 */

const ALL_PAGES: readonly Page[] = ['identity', 'names', 'birth', 'addresses', 'documents']

const otherNames = (d: ApplicationData, kind: 'OtherForenames' | 'OtherSurnames') => {
  const block = d.AdditionalApplicantDetails?.[kind]
  const items = (kind === 'OtherForenames' ? (block as any)?.OtherForename : (block as any)?.OtherSurname) as Nullable<Nullable<{ Name?: Nullable<string>, UsedFrom?: Nullable<string>, UsedTo?: Nullable<string> }>[]>
  const element = kind === 'OtherForenames' ? 'OtherForename' : 'OtherSurname'
  return (items ?? []).map((item, i) => ({ item, path: ['AdditionalApplicantDetails', kind, element, i] as IssuePath }))
}

const previousAddresses = (d: ApplicationData) =>
  (d.PreviousAddress ?? []).map((item, i) => ({ item, path: ['PreviousAddress', i] as IssuePath }))

// --- Dates -------------------------------------------------------------------------------------------------------

type DateField = { page: Page, resolution: DateResolution, path: IssuePath, value: Nullable<string>, againstBirth: boolean }

/** Every date the DBS receives, with the page it is entered on and the resolution it is held at */
const dateFields = (d: ApplicationData): DateField[] => {
  const fields: DateField[] = []
  const add = (page: Page, resolution: DateResolution, path: IssuePath, value: Nullable<string>, againstBirth = true) =>
    fields.push({ page, resolution, path, value, againstBirth })
  const aad = d.AdditionalApplicantDetails
  add('names', 'year', ['AdditionalApplicantDetails', 'BirthSurnameUntil'], aad?.BirthSurnameUntil)
  otherNames(d, 'OtherForenames').forEach(({ item, path }) => {
    add('names', 'year', [...path, 'UsedFrom'], item?.UsedFrom)
    add('names', 'year', [...path, 'UsedTo'], item?.UsedTo)
  })
  otherNames(d, 'OtherSurnames').forEach(({ item, path }) => {
    add('names', 'year', [...path, 'UsedFrom'], item?.UsedFrom)
    add('names', 'year', [...path, 'UsedTo'], item?.UsedTo)
  })
  add('birth', 'day', ['DateOfBirth'], d.DateOfBirth, false)
  add('addresses', 'month', ['CurrentAddress', 'ResidentFromGyearMonth'], d.CurrentAddress?.ResidentFromGyearMonth)
  previousAddresses(d).forEach(({ item, path }) => {
    add('addresses', 'month', [...path, 'ResidentDates', 'ResidentToGyearMonth'], item?.ResidentDates?.ResidentToGyearMonth)
    add('addresses', 'month', [...path, 'ResidentDates', 'ResidentFromGyearMonth'], item?.ResidentDates?.ResidentFromGyearMonth)
  })
  const passport = d.ApplicantIdentityDetails?.PassportDetails
  const licence = d.ApplicantIdentityDetails?.DriverLicenceDetails
  add('documents', 'day', ['ApplicantIdentityDetails', 'PassportDetails', 'PassportIssueDate'], passport?.PassportIssueDate)
  add('documents', 'day', ['ApplicantIdentityDetails', 'PassportDetails', 'PassportDob'], passport?.PassportDob)
  add('documents', 'day', ['ApplicantIdentityDetails', 'DriverLicenceDetails', 'DriverLicenceValidFrom'], licence?.DriverLicenceValidFrom)
  add('documents', 'day', ['ApplicantIdentityDetails', 'DriverLicenceDetails', 'DriverLicenceDOB'], licence?.DriverLicenceDOB)
  return fields
}

const dateRule = (id: 'date.before1900' | 'date.future' | 'date.beforeBirth', problem: DateProblem, title: string, dbsCodes: number[], spec: string[]): Rule<ApplicationData> => ({
  id, title, dbsCodes, spec, severity: 'error', pages: ALL_PAGES.filter(p => p !== 'identity'), stage: 'page',
  check: (d, ctx) => dateFields(d)
    .filter(f => owned(ctx, f.page))
    .filter(f => dateProblem(f.resolution, f.value, f.againstBirth ? d.DateOfBirth : undefined, ctx.today) === problem)
    .map(f => ({ path: f.path, message: message(id) })),
})

const isIsoDate = (value: Nullable<string>): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

// --- The rules ---------------------------------------------------------------------------------------------------

export const applicationRules: Rule<ApplicationData>[] = [
  // 1. Helpful checks that should be seen before a bare character-set message (DBS-322)
  {
    id: 'county.notCountry.uk', title: 'A UK nation or the UK typed as a county', dbsCodes: [], spec: [],
    severity: 'error', pages: ['birth', 'addresses'], stage: 'page',
    check: (d, ctx) => countyIssues(d, ctx, 'uk'),
  },
  {
    id: 'county.notCountry.repeat', title: 'The chosen country repeated as the county', dbsCodes: [], spec: [],
    severity: 'error', pages: ['birth', 'addresses'], stage: 'page',
    check: (d, ctx) => countyIssues(d, ctx, 'repeat'),
  },

  // 2. The XSD layer, generated from the DBS's schema
  {
    id: 'field.constraints', title: 'XSD field constraints: lengths, characters and permitted values', dbsCodes: [102, 109, 110, 111], spec: ['XSD'],
    severity: 'error', pages: ALL_PAGES, stage: 'page',
    check: (d, ctx) => fieldConstraintIssues(d, ctx.at === 'confirm' || ctx.at === 'server' ? undefined : [ctx.at]),
  },
  {
    id: 'field.required', title: 'XSD required elements present on a finished application', dbsCodes: [], spec: ['XSD'],
    severity: 'error', pages: [], stage: 'server',
    check: (d, ctx) => ctx.complete ? requiredFieldIssues(d) : [],
  },
  {
    id: 'text.upperCase', title: 'Upper case where the XSD character set is upper case only', dbsCodes: [102], spec: ['XSD'],
    severity: 'error', pages: [], stage: 'server',
    check: d => upperCaseIssues(d),
  },

  // 3. Business rules: dates
  dateRule('date.before1900', 'before1900', 'Years must begin 19 or 20', [104], ['4#3']),
  dateRule('date.future', 'future', 'No date may be in the future', [103], ['4#2', '4#5', '4#8', '4#10', '4#12', '4#15', '4#17', '4#22', '4#24', '4#29', '4#31']),
  dateRule('date.beforeBirth', 'beforeBirth', 'No date may be before the applicant\'s date of birth', [105], ['4#9', '4#11', '4#13', '4#16', '4#18', '4#23', '4#25']),
  {
    id: 'dob.under16', title: 'The applicant must be at least 16', dbsCodes: [108], spec: ['4#4'],
    severity: 'error', pages: ['birth'], stage: 'page',
    check: (d, ctx) => isIsoDate(d.DateOfBirth) && !isOver16(d.DateOfBirth, ctx.today) ? [{ path: ['DateOfBirth'], message: message('dob.under16') }] : [],
  },
  {
    id: 'dob.over110', title: 'The applicant must be at most 110', dbsCodes: [108], spec: ['4#4'],
    severity: 'error', pages: ['birth'], stage: 'page',
    check: (d, ctx) => isIsoDate(d.DateOfBirth) && !isUnder110(d.DateOfBirth, ctx.today) ? [{ path: ['DateOfBirth'], message: message('dob.over110') }] : [],
  },

  // Identity
  {
    id: 'ni.format', title: 'National Insurance number in the HMRC format', dbsCodes: [102], spec: ['4#6', '3.7.3'],
    severity: 'error', pages: ['identity'], stage: 'page',
    check: d => present(d.NiNumber) && isValidNationalInsuranceNumber(d.NiNumber) === 'no-invalid-format' ? [{ path: ['NiNumber'], message: message('ni.format') }] : [],
  },
  {
    id: 'ni.prefix', title: 'National Insurance number prefix HMRC never issues', dbsCodes: [102], spec: ['4#6', '3.7.3'],
    severity: 'error', pages: ['identity'], stage: 'page',
    check: d => present(d.NiNumber) && isValidNationalInsuranceNumber(d.NiNumber) === 'no-invalid-start' ? [{ path: ['NiNumber'], message: message('ni.prefix') }] : [],
  },
  {
    id: 'contact.format', title: 'Contact number is a UK number', dbsCodes: [], spec: [],
    severity: 'error', pages: ['identity'], stage: 'page',
    check: d => {
      const number = d.AdditionalApplicantDetails?.ContactNumber
      return present(number) && !/^0([1-9]\d{8,10})$/.test(number) ? [{ path: ['AdditionalApplicantDetails', 'ContactNumber'], message: message('contact.format') }] : []
    },
  },
  {
    id: 'language.englishOnly', title: 'Online applications are English only', dbsCodes: [], spec: [],
    severity: 'error', pages: ['identity'], stage: 'page',
    check: d => d.AdditionalApplicantDetails?.LanguagePreference === 'welsh' ? [{ path: ['AdditionalApplicantDetails', 'LanguagePreference'], message: message('language.englishOnly') }] : [],
  },
  {
    id: 'title.gender', title: 'Title and gender agree', dbsCodes: [], spec: [],
    severity: 'error', pages: ['identity', 'names'], stage: 'page',
    check: d => {
      const title = d.Title
      const gender = d.Gender
      if (!present(title) || !present(gender)) return []
      const clash = (['MR'].includes(title) && gender === 'female') || (['MRS', 'MISS', 'MS'].includes(title) && gender === 'male')
      if (!clash) return []
      const text = message('title.gender', title, gender)
      return [{ path: ['Gender'], message: text }, { path: ['Title'], message: text }]
    },
  },

  // Names
  {
    id: 'names.birthSurname.title', title: 'Mrs and Ms need a birth surname', dbsCodes: [], spec: ['4#7'],
    severity: 'error', pages: ['names'], stage: 'page',
    check: d => present(d.Title) && ['MRS', 'MS'].includes(d.Title) && !present(d.AdditionalApplicantDetails?.BirthSurname)
      ? [{ path: ['AdditionalApplicantDetails', 'BirthSurname'], message: message('names.birthSurname.title', d.Title) }]
      : [],
  },
  {
    id: 'names.birthSurname.female', title: 'A female applicant not titled Miss needs a birth surname', dbsCodes: [], spec: ['4#7'],
    severity: 'error', pages: ['names'], stage: 'page',
    check: d => d.Gender === 'female' && d.Title !== 'MISS' && !present(d.AdditionalApplicantDetails?.BirthSurname)
      ? [{ path: ['AdditionalApplicantDetails', 'BirthSurname'], message: message('names.birthSurname.female') }]
      : [],
  },
  {
    id: 'names.otherName.order', title: 'An other name\'s "used to" is not before its "used from"', dbsCodes: [106], spec: ['4#14', '4#19'],
    severity: 'error', pages: ['names'], stage: 'page',
    check: d => [...otherNames(d, 'OtherForenames'), ...otherNames(d, 'OtherSurnames')]
      .filter(({ item }) => present(item?.UsedFrom) && present(item?.UsedTo) && (item?.UsedFrom as string) > (item?.UsedTo as string))
      .map(({ path }) => ({ path: [...path, 'UsedTo'], message: message('names.otherName.order') })),
  },
  {
    id: 'text.placeholder', title: 'No placeholder text in optional fields', dbsCodes: [], spec: [],
    severity: 'error', pages: ['names', 'birth', 'addresses'], stage: 'page',
    check: (d, ctx) => {
      const issues: RawIssue[] = []
      const refuse = (path: IssuePath, value: Nullable<string>, tokens: string[]) => {
        const placeholder = placeholderIn(value, tokens)
        if (placeholder !== null) issues.push({ path, message: message('text.placeholder', placeholder) })
      }
      const aad = d.AdditionalApplicantDetails
      if (owned(ctx, 'names')) {
        (d.Middlenames?.Middlename ?? []).forEach((m, i) => refuse(['Middlenames', 'Middlename', i], m, NAME_PLACEHOLDER_TOKENS))
        refuse(['AdditionalApplicantDetails', 'BirthSurname'], aad?.BirthSurname, NAME_PLACEHOLDER_TOKENS)
        otherNames(d, 'OtherForenames').forEach(({ item, path }) => refuse([...path, 'Name'], item?.Name, NAME_PLACEHOLDER_TOKENS))
        otherNames(d, 'OtherSurnames').forEach(({ item, path }) => refuse([...path, 'Name'], item?.Name, NAME_PLACEHOLDER_TOKENS))
      }
      if (owned(ctx, 'birth')) {
        refuse(['AdditionalApplicantDetails', 'BirthCounty'], aad?.BirthCounty, PLACEHOLDER_TOKENS)
        refuse(['AdditionalApplicantDetails', 'BirthNationality'], aad?.BirthNationality, PLACEHOLDER_TOKENS)
      }
      if (owned(ctx, 'addresses')) {
        refuse(['CurrentAddress', 'Address', 'AddressLine2'], d.CurrentAddress?.Address?.AddressLine2, PLACEHOLDER_TOKENS)
        refuse(['CurrentAddress', 'Address', 'AddressCounty'], d.CurrentAddress?.Address?.AddressCounty, PLACEHOLDER_TOKENS)
        previousAddresses(d).forEach(({ item, path }) => {
          refuse([...path, 'Address', 'AddressLine2'], item?.Address?.AddressLine2, PLACEHOLDER_TOKENS)
          refuse([...path, 'Address', 'AddressCounty'], item?.Address?.AddressCounty, PLACEHOLDER_TOKENS)
        })
      }
      return issues
    },
  },

  // Addresses
  {
    id: 'address.postcode.required', title: 'A UK address has a postcode', dbsCodes: [101], spec: ['4#21'],
    severity: 'error', pages: ['addresses'], stage: 'page',
    check: d => addressIssues(d, (address, path) => address?.CountryCode === 'GB' && !present(address?.Postcode)
      ? [{ path: [...path, 'Postcode'], message: message('address.postcode.required') }]
      : []),
  },
  {
    id: 'address.postcode.format', title: 'A UK postcode is in the UK format', dbsCodes: [101], spec: ['4#21', '3.7.4'],
    severity: 'error', pages: ['addresses'], stage: 'page',
    check: d => addressIssues(d, (address, path) => address?.CountryCode === 'GB' && present(address?.Postcode) && !isValidPostcode(address?.Postcode)
      ? [{ path: [...path, 'Postcode'], message: message('address.postcode.format') }]
      : []),
  },
  {
    id: 'address.previous.order', title: 'A previous address\'s "from" is not after its "to"', dbsCodes: [106], spec: ['4#26'],
    severity: 'error', pages: ['addresses'], stage: 'page',
    check: d => previousAddresses(d)
      .filter(({ item }) => present(item?.ResidentDates?.ResidentFromGyearMonth) && present(item?.ResidentDates?.ResidentToGyearMonth)
        && (item?.ResidentDates?.ResidentFromGyearMonth as string) > (item?.ResidentDates?.ResidentToGyearMonth as string))
      .map(({ path }) => ({ path: [...path, 'ResidentDates', 'ResidentFromGyearMonth'], message: message('address.previous.order') })),
  },
  {
    id: 'address.gaps', title: 'No gaps in the address history', dbsCodes: [107], spec: ['4#20'],
    severity: 'error', pages: ['addresses'], stage: 'page',
    check: (d, ctx) => {
      if (!present(d.CurrentAddress?.ResidentFromGyearMonth) || d.PreviousAddress == null) return []
      const ranges = [
        { from: d.CurrentAddress?.ResidentFromGyearMonth as string, to: ctx.today.substring(0, 7) },
        ...previousAddresses(d).map(({ item }) => ({ from: item?.ResidentDates?.ResidentFromGyearMonth ?? '', to: item?.ResidentDates?.ResidentToGyearMonth ?? '' })),
      ]
      return findGapsInMonthRanges(ranges).length > 0 ? [{ path: ['PreviousAddress'], message: message('address.gaps') }] : []
    },
  },
  {
    id: 'address.fiveYears', title: 'The address history covers five years', dbsCodes: [107], spec: ['4#20'],
    severity: 'error', pages: [], stage: 'confirm',
    check: (d, ctx) => {
      const years = yearsCoveredSince([d.CurrentAddress?.ResidentFromGyearMonth, ...previousAddresses(d).map(({ item }) => item?.ResidentDates?.ResidentFromGyearMonth)], ctx.today)
      return years !== undefined && years < 5 ? [{ path: ['PreviousAddress'], message: message('address.fiveYears', years) }] : []
    },
  },
  {
    id: 'address.nfa.notGB', title: 'No fixed abode is recorded outside the UK (DBS-310)', dbsCodes: [], spec: [],
    severity: 'error', pages: ['addresses'], stage: 'page',
    check: d => previousAddresses(d)
      .filter(({ item }) => (item?.Address?.AddressLine1 ?? '').trim().toUpperCase() === 'NO FIXED ABODE' && item?.Address?.CountryCode === 'GB')
      .map(({ path }) => ({ path: [...path, 'Address', 'CountryCode'], message: message('address.nfa.notGB') })),
  },

  // Documents
  {
    id: 'documents.licence.number', title: 'A GB driver licence number encodes the applicant\'s names, birth date and gender', dbsCodes: [102], spec: ['3.7.1'],
    severity: 'error', pages: ['documents'], stage: 'page',
    check: d => {
      const licence = d.ApplicantIdentityDetails?.DriverLicenceDetails
      if (!present(licence?.DriverLicenceNumber) || licence?.DriverLicenceIssueCountry !== 'GB') return []
      return isValidDriverLicenceNumber(licence.DriverLicenceNumber as string, d) === 'yes'
        ? []
        : [{ path: ['ApplicantIdentityDetails', 'DriverLicenceDetails', 'DriverLicenceNumber'], message: message('documents.licence.number') }]
    },
  },
  {
    id: 'documents.dob.passport', title: 'The passport date of birth is the applicant\'s', dbsCodes: [116], spec: ['4#28'],
    severity: 'error', pages: [], stage: 'confirm',
    check: d => documentDobIssue(d, 'documents.dob.passport', d.ApplicantIdentityDetails?.PassportDetails?.PassportDob, ['ApplicantIdentityDetails', 'PassportDetails', 'PassportDob']),
  },
  {
    id: 'documents.dob.licence', title: 'The driver licence date of birth is the applicant\'s', dbsCodes: [116], spec: ['4#30'],
    severity: 'error', pages: [], stage: 'confirm',
    check: d => documentDobIssue(d, 'documents.dob.licence', d.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceDOB, ['ApplicantIdentityDetails', 'DriverLicenceDetails', 'DriverLicenceDOB']),
  },

  // Declaration
  {
    id: 'declaration.applicant', title: 'The applicant has declared the application complete and true', dbsCodes: [], spec: ['4#27'],
    severity: 'error', pages: [], stage: 'confirm',
    check: d => d.AdditionalApplicantDetails?.DeclarationByApplicant !== 'y' ? [{ path: ['AdditionalApplicantDetails', 'DeclarationByApplicant'], message: message('declaration.applicant') }] : [],
  },
  {
    id: 'declaration.unspentConvictions', title: 'The unspent convictions question is answered (DBS-261)', dbsCodes: [], spec: [],
    severity: 'error', pages: [], stage: 'confirm',
    check: d => {
      const answer = d.AdditionalApplicantDetails?.UnspentConvictions
      return answer === 'y' || answer === 'n' ? [] : [{ path: ['AdditionalApplicantDetails', 'UnspentConvictions'], message: message('declaration.unspentConvictions') }]
    },
  },

  // Registered Body side
  {
    id: 'identity.verified', title: 'Identity verified before sending', dbsCodes: [], spec: ['4#32'],
    severity: 'error', pages: [], stage: 'server',
    check: (d, ctx) => ctx.complete && d.ApplicantIdentityDetails?.IdentityVerified !== 'y'
      ? [{ path: ['ApplicantIdentityDetails', 'IdentityVerified'], message: message('identity.verified') }]
      : [],
  },
  {
    id: 'rb.number', title: 'Registered Body number is 11 digits', dbsCodes: [117], spec: ['4#33'],
    severity: 'error', pages: [], stage: 'server',
    check: (_d, ctx) => ctx.rb !== undefined && !/^\d{11}$/.test(ctx.rb.rbNumber ?? '') ? [{ path: ['RBdetails', 'RBNumber'], message: message('rb.number') }] : [],
  },
  {
    id: 'rb.csNumber', title: 'Countersignatory number is 11 digits', dbsCodes: [113, 114, 115], spec: ['4#34'],
    severity: 'error', pages: [], stage: 'server',
    check: (_d, ctx) => ctx.rb !== undefined && !/^\d{11}$/.test(ctx.rb.csNumber ?? '') ? [{ path: ['RBdetails', 'CSigNumber'], message: message('rb.csNumber') }] : [],
  },
  {
    id: 'rb.addressChecked', title: 'Current address details checked before sending', dbsCodes: [], spec: ['4#35'],
    severity: 'error', pages: [], stage: 'server',
    check: (_d, ctx) => ctx.rb !== undefined && ctx.rb.addressDetailsChecked !== true ? [{ path: ['RBdetails', 'CurrentAddressDetailsChecked'], message: message('rb.addressChecked') }] : [],
  },

  // Warnings: never blocking, shown to the applicant, the countersignatory and in the gates' reports (DBS-263)
  {
    id: 'names.warning', title: 'Name sanity warnings: a title in a name, a value typed twice, the same name in two fields', dbsCodes: [], spec: [],
    severity: 'warning', pages: [], stage: 'confirm',
    check: d => findNameWarnings(d).map(w => ({ path: [w.field], message: message('names.warning', w.message) })),
  },
]

// --- Shared check bodies -----------------------------------------------------------------------------------------

type AddressLike = Nullable<{ Postcode?: Nullable<string>, CountryCode?: Nullable<string>, AddressCounty?: Nullable<string> }>

/** Runs `fn` over the current address and every previous address, with the path to its Address block */
const addressIssues = (d: ApplicationData, fn: (address: AddressLike, path: IssuePath) => RawIssue[]): RawIssue[] => [
  ...fn(d.CurrentAddress?.Address, ['CurrentAddress', 'Address']),
  ...previousAddresses(d).flatMap(({ item, path }) => fn(item?.Address, [...path, 'Address'])),
]

const countyIssues = (d: ApplicationData, ctx: RuleContext, kind: 'uk' | 'repeat'): RawIssue[] => {
  const issues: RawIssue[] = []
  const refuse = (path: IssuePath, county: Nullable<string>, countryCode: Nullable<string>) => {
    const problem = countyCountryProblemKind(county, countryCode, ctx.countryLabelFor)
    if (problem !== null && problem.kind === kind) {
      issues.push({ path, message: kind === 'uk' ? message('county.notCountry.uk', problem.typed) : message('county.notCountry.repeat', problem.typed) })
    }
  }
  if (owned(ctx, 'birth')) {
    refuse(['AdditionalApplicantDetails', 'BirthCounty'], d.AdditionalApplicantDetails?.BirthCounty, d.AdditionalApplicantDetails?.BirthCountry)
  }
  if (owned(ctx, 'addresses')) {
    refuse(['CurrentAddress', 'Address', 'AddressCounty'], d.CurrentAddress?.Address?.AddressCounty, d.CurrentAddress?.Address?.CountryCode)
    previousAddresses(d).forEach(({ item, path }) => refuse([...path, 'Address', 'AddressCounty'], item?.Address?.AddressCounty, item?.Address?.CountryCode))
  }
  return issues
}

/**
 * The message for a document date of birth that differs from the applicant's, or null when they agree (or either is
 * missing). The Documents page shows this as a warning while the applicant is still entering; the confirm screen and
 * every later gate refuse it (DBS-264).
 */
export const documentDateOfBirthMismatch = (document: 'passport' | 'driver licence', documentDob: Nullable<string>, dateOfBirth: Nullable<string>): string | null => {
  if (!present(documentDob) || !present(dateOfBirth)) return null
  if (documentDob.substring(0, 10) === dateOfBirth.substring(0, 10)) return null
  return message(document === 'passport' ? 'documents.dob.passport' : 'documents.dob.licence', documentDob, dateOfBirth)
}

const documentDobIssue = (d: ApplicationData, id: 'documents.dob.passport' | 'documents.dob.licence', documentDob: Nullable<string>, path: IssuePath): RawIssue[] => {
  const mismatch = documentDateOfBirthMismatch(id === 'documents.dob.passport' ? 'passport' : 'driver licence', documentDob, d.DateOfBirth)
  return mismatch === null ? [] : [{ path, message: mismatch }]
}

// --- Evaluation --------------------------------------------------------------------------------------------------

export const todayISO = (): string => new Date().toISOString().substring(0, 10)

/** Does this rule run for an evaluation at this point? */
export const ruleApplies = (rule: Pick<Rule<unknown>, 'pages' | 'stage'>, at: At): boolean =>
  at === 'server' ? true
    : at === 'confirm' ? rule.stage !== 'server'
      : rule.stage === 'page' && rule.pages.indexOf(at) >= 0

/**
 * Runs every rule that applies at `options.at` over the application data and returns the issues, each tagged with its
 * rule. Errors and warnings are both returned; callers that block should look at `severity`.
 */
export const evaluateRules = (data: ApplicationData, options: EvaluateOptions): Issue[] => {
  const ctx: RuleContext = {
    at: options.at,
    today: options.today ?? todayISO(),
    complete: options.complete ?? false,
    countryLabelFor: options.countryLabelFor,
    rb: options.rb,
  }
  const issues: Issue[] = []
  const seen: Record<string, true> = {}
  for (let r = 0; r < applicationRules.length; r++) {
    const rule = applicationRules[r]
    if (!ruleApplies(rule, ctx.at)) continue
    const raws = rule.check(data, ctx)
    for (let i = 0; i < raws.length; i++) {
      const raw = raws[i]
      const key = `${rule.id}|${raw.path.join('.')}|${raw.message}`
      if (seen[key]) continue
      seen[key] = true
      issues.push({ ruleId: rule.id, path: raw.path, message: raw.message, severity: rule.severity, dbsCodes: rule.dbsCodes })
    }
  }
  return issues
}

/** The errors only (the portals block on these; warnings are shown, never blocking) */
export const evaluateErrors = (data: ApplicationData, options: EvaluateOptions): Issue[] =>
  evaluateRules(data, options).filter(issue => issue.severity === 'error')

export const ruleById = (id: string): Rule<ApplicationData> | undefined => applicationRules.find(rule => rule.id === id)
